import { useEffect, useState, useRef } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { SingleSelectList } from '../components/SingleSelectList';
import { CheckboxList } from '../components/CheckboxList';
import { SearchableDropdown } from '../components/SearchableDropdown';
import { MultiDropdownChips } from '../components/MultiDropdownChips';
import { FreeTextInput } from '../components/FreeTextInput';
import { QUIZ } from '../data/quiz.generated';
import { DROPDOWN_OPTIONS } from '../data/dropdownOptions';
import type { DropdownOption } from '../data/dropdownOptions';
import { getNextQuestionId } from '../lib/routing';
import { useQuizStore } from '../store/quizStore';
import { AUTO_ADVANCE_MS } from '../lib/motion';
import { track, trackQuizComplete } from '../lib/analytics';
import type { Question, AnswerOption } from '../data/quizTypes';

/** Prefix used by SearchableDropdown / MultiDropdownChips for free-typed values. */
const FREE_PREFIX = '__free__:';

export function QuestionPage() {
  const { questionId } = useParams<{ questionId: string }>();
  const navigate = useNavigate();
  const sessionId = useQuizStore((s) => s.sessionId);
  const answer = useQuizStore((s) => (questionId ? s.answers[questionId] : undefined));
  const ensureSession = useQuizStore((s) => s.ensureSession);
  const setAnswer = useQuizStore((s) => s.setAnswer);
  const setVariable = useQuizStore((s) => s.setVariable);
  const setCurrentQuestionId = useQuizStore((s) => s.setCurrentQuestionId);

  // Set current question id in store on mount
  useEffect(() => {
    ensureSession();
    if (questionId) setCurrentQuestionId(questionId);
  }, [questionId, ensureSession, setCurrentQuestionId]);

  // URL guard: if no session and not entry, push back to /
  if (!questionId) return <Navigate to="/" replace />;
  const q = QUIZ.questions[questionId];
  if (!q) return <Navigate to="/" replace />;
  // Allow direct nav to entry. Otherwise, require an active session.
  if (!sessionId && questionId !== QUIZ.entryQuestionId) {
    return <Navigate to="/" replace />;
  }

  const advance = (answerOptionId: string | null) => {
    track('question_answered', { questionId, type: q.type, answerId: answerOptionId });
    const result = getNextQuestionId(questionId, answerOptionId, useQuizStore.getState());
    if (result.kind === 'complete') {
      useQuizStore.getState().markQuizCompleted();
      // Paying customer retaking from Account & billing: no lead capture,
      // no pricing, and no funnel conversion event — straight to the rebuild.
      if (useQuizStore.getState().retake) {
        track('retake_quiz_completed', { from: questionId });
        navigate('/retake/building');
        return;
      }
      track('quiz_completed', { from: questionId });
      // jobtest.org GTM doc-specified event.
      trackQuizComplete();
      navigate('/lead');
    } else {
      navigate(`/q/${result.questionId}`);
    }
  };

  /**
   * Resolves a routing ID for `advance()`.
   *
   * DROPDOWN_OPTIONS IDs (e.g. "major-accounting") and free-text IDs
   * ("__free__:...") are not in q.options, so the routing engine can't find
   * a nextQuestionId for them. In that case we fall back to the noMatch
   * sentinel option — dropdown questions use it as the universal "next".
   */
  const routingIdFor = (rawId: string | null): string | null => {
    if (!rawId) return q.options.find((o) => o.noMatch)?.id ?? q.options[0]?.id ?? null;
    const isKnownOption = q.options.some((o) => o.id === rawId);
    if (isKnownOption) return rawId;
    return q.options.find((o) => o.noMatch)?.id ?? q.options[0]?.id ?? null;
  };

  // Pre-compute the DROPDOWN_OPTIONS override list for this question (or null).
  const dropdownOptions = DROPDOWN_OPTIONS[questionId] ?? null;

  return (
    <QuizLayout>
      <h1
        className="text-display-md text-ink mb-4"
        // q.title may contain whitelisted inline HTML (strong/em/u/br)
        // — already sanitized at build time.
        dangerouslySetInnerHTML={{ __html: q.title }}
      />
      {q.description && (
        <p
          className="text-ink-soft text-base mb-7"
          dangerouslySetInnerHTML={{ __html: q.description }}
        />
      )}
      {q.descriptionImageUrl && (
        <div className="mb-7 flex justify-center">
          <img
            src={q.descriptionImageUrl}
            alt=""
            className="max-w-full max-h-[360px] rounded-2xl border-2 border-ink/10 bg-canvas-subtle"
            loading="eager"
          />
        </div>
      )}

      <QuestionWidget
        question={q}
        answer={answer}
        onSingleSelect={(opt) => {
          setAnswer(questionId, { optionIds: [opt.id], ts: Date.now() });
          if (q.variable) setVariable(q.variable, opt.value ?? opt.text);
          if (q.autoAdvance) {
            window.setTimeout(() => advance(opt.id), AUTO_ADVANCE_MS());
          }
        }}
        onMultiSelectChange={(ids) => {
          setAnswer(questionId, { optionIds: ids, ts: Date.now() });
          if (q.variable) {
            const labels = ids
              .map((id) => {
                // Free-text chip: strip the prefix to get the human label.
                if (id.startsWith(FREE_PREFIX)) return id.slice(FREE_PREFIX.length);
                // Check DROPDOWN_OPTIONS override first (these IDs aren't in q.options).
                const dropOpt = dropdownOptions?.find((o) => o.id === id);
                if (dropOpt) return dropOpt.label;
                const o = q.options.find((opt) => opt.id === id);
                return o?.value ?? o?.text ?? id;
              })
              .join('|');
            setVariable(q.variable, labels);
          }
        }}
        onFreeTextChange={(text) => {
          setAnswer(questionId, {
            optionIds: q.options.length > 0 ? [q.options[0].id] : [],
            freeText: text,
            ts: Date.now(),
          });
          if (q.variable) setVariable(q.variable, text);
        }}
        onDropdownSelect={(id, label) => {
          setAnswer(questionId, { optionIds: [id], ts: Date.now() });
          if (q.variable) setVariable(q.variable, label);
        }}
        dropdownOptions={dropdownOptions}
        onContinue={() => {
          // For multi-select, we need to pass *some* answer id to satisfy
          // the routing engine — none of the per-answer routes diverge in
          // multi-choice (the CSV doesn't model "different next per
          // checkbox combination" — that lives in computedBranches). Use
          // the first selected, or the first option as a fallback.
          // routingIdFor() normalises DROPDOWN_OPTIONS / __free__: IDs.
          const raw = answer?.optionIds[0] ?? null;
          advance(routingIdFor(raw));
        }}
        onSkip={() => advance(q.options[0]?.id ?? null)}
      />
    </QuizLayout>
  );
}

interface WidgetProps {
  question: Question;
  answer: { optionIds: string[]; freeText?: string } | undefined;
  onSingleSelect: (option: AnswerOption) => void;
  onMultiSelectChange: (ids: string[]) => void;
  onFreeTextChange: (text: string) => void;
  onContinue: () => void;
  onSkip: () => void;
  /** Override option list from dropdownOptions.ts (null = use q.options). */
  dropdownOptions: DropdownOption[] | null;
  /** Called when user picks from DROPDOWN_OPTIONS or types a free-text value. */
  onDropdownSelect: (id: string, label: string) => void;
}

function QuestionWidget({
  question: q,
  answer,
  onSingleSelect,
  onMultiSelectChange,
  onFreeTextChange,
  onContinue,
  onSkip,
  dropdownOptions,
  onDropdownSelect,
}: WidgetProps) {
  const items = q.options
    .filter((o) => !o.noMatch && o.text !== '')
    .map((o) => ({ id: o.id, label: o.text, imageUrl: o.imageUrl }));

  // _custom = numeric salary input
  if (q.type === '_custom') {
    return (
      <FreeTextInputBlock
        question={q}
        value={answer?.freeText ?? ''}
        onChange={onFreeTextChange}
        onContinue={onContinue}
        onSkip={onSkip}
        prefix="$"
        numeric
        singleLine
        placeholder="e.g. 75000"
      />
    );
  }

  if (q.type === '_freeText') {
    return (
      <FreeTextInputBlock
        question={q}
        value={answer?.freeText ?? ''}
        onChange={onFreeTextChange}
        onContinue={onContinue}
        onSkip={onSkip}
        placeholder={q.hint ?? 'Type your answer here'}
      />
    );
  }

  if (q.type === '_transition') {
    const transitionImg = q.options[0]?.imageUrl;
    return (
      <>
        {transitionImg && (
          <div className="mb-8 flex justify-center">
            <img
              src={transitionImg}
              alt=""
              className="max-w-full max-h-[360px] rounded-2xl object-contain"
              loading="eager"
            />
          </div>
        )}
        <button type="button" className="btn-primary" onClick={onContinue}>
          Continue →
        </button>
      </>
    );
  }

  if (q.type === '_singleChoice') {
    return (
      <SingleSelectList
        options={items}
        value={answer?.optionIds[0]}
        onChange={(id) => {
          const opt = q.options.find((o) => o.id === id);
          if (opt) onSingleSelect(opt);
        }}
      />
    );
  }

  if (q.type === '_multiChoice') {
    return (
      <>
        <CheckboxList options={items} values={answer?.optionIds ?? []} onChange={onMultiSelectChange} />
        <div className="mt-6">
          <button
            type="button"
            className="btn-primary"
            disabled={!answer?.optionIds.length && !q.skipping}
            onClick={onContinue}
          >
            Continue →
          </button>
        </div>
        {q.skipping && (
          <button type="button" className="btn-ghost mt-3 mx-auto" onClick={onSkip}>
            Skip this question
          </button>
        )}
      </>
    );
  }

  if (q.type === '_dropdown') {
    // Use DROPDOWN_OPTIONS override if available; otherwise fall back to CSV items.
    const ddItems = dropdownOptions ?? items;
    return (
      <>
        <SearchableDropdown
          options={ddItems}
          value={answer?.optionIds[0]}
          onChange={(id) => {
            if (id.startsWith(FREE_PREFIX)) {
              // Free-typed value — store it as a custom selection.
              onDropdownSelect(id, id.slice(FREE_PREFIX.length));
            } else {
              // Try DROPDOWN_OPTIONS override first.
              const dropOpt = dropdownOptions?.find((o) => o.id === id);
              if (dropOpt) {
                onDropdownSelect(id, dropOpt.label);
              } else {
                const opt = q.options.find((o) => o.id === id);
                if (opt) onSingleSelect(opt);
              }
            }
          }}
          placeholder={q.hint ?? 'Search…'}
        />
        <div className="mt-6">
          <button type="button" className="btn-primary" disabled={!answer?.optionIds.length} onClick={onContinue}>
            Continue →
          </button>
        </div>
      </>
    );
  }

  if (q.type === '_multiDropdown') {
    // Use DROPDOWN_OPTIONS override if available; otherwise fall back to CSV items.
    const ddItems = dropdownOptions ?? items;
    return (
      <>
        <MultiDropdownChips
          options={ddItems}
          values={answer?.optionIds ?? []}
          onChange={onMultiSelectChange}
          placeholder={q.hint ?? 'Search and add…'}
        />
        <div className="mt-6">
          <button
            type="button"
            className="btn-primary"
            disabled={!answer?.optionIds.length && !q.skipping}
            onClick={onContinue}
          >
            Continue →
          </button>
        </div>
        {q.skipping && (
          <button type="button" className="btn-ghost mt-3 mx-auto" onClick={onSkip}>
            Skip this question
          </button>
        )}
      </>
    );
  }

  return <div className="text-err">Unsupported question type: {q.type}</div>;
}

interface FreeTextBlockProps {
  question: Question;
  value: string;
  onChange: (s: string) => void;
  onContinue: () => void;
  onSkip: () => void;
  placeholder?: string;
  prefix?: string;
  numeric?: boolean;
  singleLine?: boolean;
}

function FreeTextInputBlock({
  question: q,
  value,
  onChange,
  onContinue,
  onSkip,
  placeholder,
  prefix,
  numeric,
  singleLine,
}: FreeTextBlockProps) {
  // Local state mirrors store so the input stays responsive even though
  // we only persist on debounce. We push to store on each keystroke.
  const [local, setLocal] = useState(value);
  // Keep local in sync if the store changes externally (e.g. resume).
  const lastSeen = useRef(value);
  useEffect(() => {
    if (lastSeen.current !== value && local !== value) {
      setLocal(value);
    }
    lastSeen.current = value;
  }, [value, local]);

  const commit = (s: string) => {
    setLocal(s);
    onChange(s);
  };

  return (
    <>
      <FreeTextInput
        value={local}
        onChange={commit}
        placeholder={placeholder}
        prefix={prefix}
        numeric={numeric}
        singleLine={singleLine}
      />
      <div className="mt-6">
        <button
          type="button"
          className="btn-primary"
          disabled={!local.trim() && !q.skipping}
          onClick={onContinue}
        >
          Continue →
        </button>
      </div>
      {q.skipping && (
        <button type="button" className="btn-ghost mt-3 mx-auto" onClick={onSkip}>
          Skip this question
        </button>
      )}
    </>
  );
}
